/* Copyright (c) 2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { AppliedFilterDefinition } from "./useAppliedFilters/types";
import { coerceToStringArray } from "./useAppliedFilters/utils";
import { QueryState } from "./useQueryState/useQueryState";

export type AppliedFilter<TKey extends string = string> = {
  key: TKey;
  label: string;
  value: string;
  values?: string[];
  valueLabels?: string[];
};

type UseAppliedFiltersArgs<TQuery extends QueryState, TKey extends string> = {
  queryState: TQuery;
  updateQueryState: (nextState: TQuery) => void;
  definitions: Array<AppliedFilterDefinition<TQuery, TKey>>;
  resetPageTo?: string;
};

export type UseAppliedFiltersResult<TKey extends string> = {
  filters: Array<AppliedFilter<TKey>>;
  onRemoveFilter: (key: TKey) => void;
  onRemoveFilterValue: (key: TKey, valueToRemove: string) => void;
  onClearAllFilters: () => void;
};

export function useAppliedFilters<
  TQuery extends QueryState,
  TKey extends string
>({
  queryState,
  updateQueryState,
  definitions,
  resetPageTo = "1",
}: UseAppliedFiltersArgs<TQuery, TKey>): UseAppliedFiltersResult<TKey> {
  const filters = useMemo<Array<AppliedFilter<TKey>>>(() => {
    return definitions.reduce<Array<AppliedFilter<TKey>>>(
      (accumulator, definition) => {
        const values = coerceToStringArray(definition.getValues(queryState));
        if (!values.length) {
          return accumulator;
        }
        const canShowValuePopover =
          definition.enableValuePopover !== false && values.length > 1;
        const getValueLabel = definition.getValueLabel;
        const valueLabels =
          canShowValuePopover && getValueLabel
            ? values.map((valueItem) => getValueLabel(valueItem, queryState))
            : undefined;

        return [
          ...accumulator,
          {
            key: definition.key,
            label: definition.label,
            value: definition.getValueDisplay(queryState, values),
            values: canShowValuePopover ? values : undefined,
            valueLabels,
          },
        ];
      },
      []
    );
  }, [definitions, queryState]);

  const applyPatchWithPageReset = (patch: Partial<TQuery>) => {
    updateQueryState({
      ...queryState,
      ...patch,
      page: resetPageTo,
    });
  };

  const onRemoveFilter = (key: TKey) => {
    const definition = definitions.find((item) => item.key === key);
    if (!definition) {
      return;
    }
    applyPatchWithPageReset(definition.clear(queryState));
  };

  const onRemoveFilterValue = (key: TKey, valueToRemove: string) => {
    const definition = definitions.find((item) => item.key === key);
    if (!definition?.removeValue) {
      return;
    }
    applyPatchWithPageReset(definition.removeValue(queryState, valueToRemove));
  };

  const onClearAllFilters = () => {
    const mergedPatch = definitions.reduce(
      (accumulator, definition) => ({
        ...accumulator,
        ...definition.clear(queryState),
      }),
      {} as Partial<TQuery>
    );

    applyPatchWithPageReset(mergedPatch);
  };

  return {
    filters,
    onRemoveFilter,
    onRemoveFilterValue,
    onClearAllFilters,
  };
}
